<script setup lang="ts">
import { computed, provide } from 'vue'
import { getColorById } from '../utils'
import { useModalStore } from '@/stores/ModalStore'
import LogoCarousel from './LogoCarousel.vue'
import CtaButton from '../CtaButton.vue'
import LogoSearchModal from './LogoSearchModal.vue'
import Section from '../Section.vue'
import {
  SectionSettings,
  Alignment,
  Section as SectionInterface,
} from '@/components/customer_page/sections/types'
import { Logo } from './LogoSelector/types'
import { LogoContent } from './types'
import duffBeerSvg from '@/components/customer_page/sections/logos/LogoSelector/duff-beer.svg'
import hooliSvg from '@/components/customer_page/sections/logos/LogoSelector/hooli.svg'
import lumonSvg from '@/components/customer_page/sections/logos/LogoSelector/lumon.svg'

interface Props {
  content?: LogoContent[]
  editable?: boolean
  sectionId: string
  settings: SectionSettings
}

const props = withDefaults(defineProps<Props>(), {
  content: () => [],
  editable: false,
  settings: () => ({
    cta: 'Learn more',
    cta_url: '#',
    show_cta: true,
    design_settings: {
      colors: [],
    },
  }),
})

const actualSettings = computed(() => ({
  alignment: 'center' as Alignment,
  title: 'Trusted by high-growth companies',
  show_title: true,
  ...props.settings,
}))

const alignmentClass = computed(() => `text-${actualSettings.value.alignment}`)

const logos = computed(() =>
  (props.content || []).map((logo: LogoContent) => logo.company_logo?.blob_url),
)
const backgroundColor = computed(() =>
  getColorById(props.settings.design_settings?.colors, 'background_color'),
)

const modalStore = useModalStore()

const openLogoSearchModal = (): void => {
  modalStore.open('logo_search_modal', { title: 'Logo Library' })
}

const emit = defineEmits<{
  (e: 'logo-removed', logo: string, index: number): void
  (e: 'logo-added', logo: string): void
  (e: 'content-selected', sectionId: string, content: LogoContent[]): void
  (e: 'content-change', payload: Partial<SectionInterface>): void
}>()

const addLogo = (logo: Logo): void => {
  const id = Math.random()
    .toString(36)
    .substring(2, 6 + 2)

  emit('logo-added', logo.src)
  emit('content-selected', props.sectionId, [
    ...(props.content || []),
    {
      company_logo: {
        id,
        filename: `logo-${id}.${logo.format}`,
        blob_url: logo.src,
      },
    },
  ])
  modalStore.close('logo_search_modal')
}

const handleHeaderChange = (text: string): void => {
  emit('content-change', {
    id: props.sectionId,
    settings: { ...actualSettings.value, title: text },
    content: props.content,
  })
}

// Provide the addLogo function to child components
provide('addLogo', addLogo)

const removeLogo = (logo: string, index: number): void => {
  const newContent = [...props.content]
  newContent.splice(index, 1)

  emit('logo-removed', logo, index)
  emit('content-selected', props.sectionId, newContent)
}
</script>

<template>
  <Section :settings="actualSettings" :editable="editable" @header-change="handleHeaderChange">
    <div class="logos-section">
      <div class="placeholder-logos" v-if="logos.length === 0">
        <img :src="duffBeerSvg" alt="Duff Beer" />
        <img :src="hooliSvg" alt="Hooli" />
        <img :src="lumonSvg" alt="Lumon" />
      </div>
      <UeTypography
        class="text-center"
        v-if="editable && logos.length === 0"
        :contrast-with-color="backgroundColor"
      >
        No logos added yet.
      </UeTypography>
      <LogoCarousel
        v-if="logos.length > 0"
        :logos="logos"
        :editable="editable"
        @logo-removed="removeLogo"
        :showControls="editable"
      />
      <div :class="alignmentClass">
        <CtaButton class="mt-5" v-if="settings.show_cta" :settings="settings" />
      </div>
      <div v-if="editable" class="mt-4 text-center">
        <button class="btn fs-sm btn-secondary" @click="openLogoSearchModal">
          Browse and add logos
        </button>
      </div>
      <LogoSearchModal />
    </div>
  </Section>
</template>

<style scoped lang="sass">
.logos-section
  margin-top: 2rem
.placeholder-logos
  display: flex
  justify-content: space-around
  align-items: center
  img
    width: 100px
    height: auto
    opacity: 0.15
</style>
